<template>
  <HeaderBar />
  <ButtonBackContainer>
    <ButtonBack :fallback="{ name: 'bookingPlan' }">{{ t('back') }}</ButtonBack>
  </ButtonBackContainer>
  <section class="carvia-container">
    <PartnerRentForm
      v-if="car && (allocation || allocationNew)"
      :car="car"
      :allocation="allocation"
      :allocation-new="allocationNew"
      :is-saving="isCreating || isUpdating"
      :is-deleting="isDeleting"
      @on-submit="handleSubmit"
      @on-delete="onDelete"
    />
    <div v-else class="flex justify-center">
      <Spinner />
    </div>
  </section>
</template>

<script setup lang="ts">
import HeaderBar from '@/components/headerbar/HeaderBar.vue';
import ButtonBack from '@/components/buttons/ButtonBack.vue';
import { useCar } from '@/queries/use-cars';
import { useUiStore } from '@/stores/ui.store';
import { computed, ref, watchEffect } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { BookingLocationType } from '@/entities/bookings/booking-location-type.enum';
import { Alert } from '@/services/alert.service';
import ButtonBackContainer from '@/components/ButtonBackContainer.vue';
import PartnerRentForm from './PartnerRentForm.vue';
import {
  useAllocation,
  useCreateAllocation,
  useDeleteAllocation,
  useUpdateAllocation,
} from '@/queries/use-allocations.ts';
import {
  AllocationType,
  type CreateAllocationDto,
  type UpdateAllocationDto,
} from '@carvia/ros-client-types';

const { t } = useI18n();
const uiStore = useUiStore();
const route = useRoute();
const router = useRouter();
const carId = route.params.carId as string;
const allocationId = ref(route.params.allocationId as string);
const allocationNew = ref(allocationId.value === 'new');
const { data: car } = useCar(carId);
const { data: allocation } = useAllocation(
  allocationId,
  computed(() => !allocationNew.value),
);

watchEffect(() => {
  uiStore.setHeaderTitle(
    t('partnerRent'),
    car.value?.vehicleType.make.name,
    car.value?.vehicleType.model,
  );
});

const { isPending: isCreating, mutateAsync: createPartnerRent } = useCreateAllocation();
const { isPending: isUpdating, mutateAsync: updatePartnerRent } = useUpdateAllocation();
const { isPending: isDeleting, mutateAsync: deletePartnerRent } = useDeleteAllocation();

const handleSubmit = async (allocation: UpdateAllocationDto) => {
  if (allocationNew.value) {
    const createPartnerRentValues: CreateAllocationDto = {
      ...allocation,
      startDate: allocation.startDate ?? new Date(),
      endDate: allocation.endDate ?? new Date(),
      carId: carId,
      type: AllocationType.PARTNER_BOOKING,
      pickupLocationType: BookingLocationType.STATION,
      dropoffLocationType: BookingLocationType.STATION,
    };
    const { id } = await createPartnerRent(createPartnerRentValues);
    allocationNew.value = false;
    allocationId.value = id;
    await router.replace({
      name: 'partnerRentEdit',
      params: { carId, allocationId: id },
    });
  } else {
    await updatePartnerRent({ id: allocationId.value, allocation });
  }
};

const onDelete = async () => {
  if (!allocation.value?.id) return;
  const alertResult = await Alert.fire({
    titleText: t('reallyDeleteBookingTitle'),
    text: t('reallyDeleteBookingText'),
    icon: 'warning',
    showDenyButton: true,
    confirmButtonText: t('delete'),
    denyButtonText: t('keep'),
  });
  if (alertResult.isConfirmed) {
    await deletePartnerRent(allocation.value.id);
    await router.replace({ name: 'bookingPlan' });
  }
};
</script>

<i18n lang="json">
{
  "en": {
    "partnerRent": "Partner Rent",
    "reallyDeleteBookingTitle": "Really remove Booking?",
    "reallyDeleteBookingText": "The Booking will be permanently deleted.",
    "keep": "Keep"
  },
  "de": {
    "partnerRent": "Partnermiete",
    "reallyDeleteBookingTitle": "Buchung wirklich entfernen?",
    "reallyDeleteBookingText": "Die Buchung wird unwiderruflich gelöscht.",
    "keep": "Behalten"
  },
  "es": {
    "partnerRent": "Alquiler partner",
    "reallyDeleteBookingTitle": "¿Eliminar la reserva?",
    "reallyDeleteBookingText": "La reserva se eliminará de forma definitiva.",
    "keep": "Conservar"
  }
}
</i18n>
